// How lists of sessions are ordered and organized (spec "The sessions list: what you're working
// on", "Session rows"). The machine ranks every session (Session.rank: what needs you, then what
// runs, then what's done, then the rest) and works out its groups (Session.row); here they're
// only sorted, bucketed and drawn. Organizing (not grouped, by folder, by program) is one
// setting for the account (me.settings.organize), the same in the CLI and the apps; ⌘1–9 and
// the palette follow the order shown.

import { useRef } from 'react';
import type { Session } from '../net/protocol';
import { useStore, type Organize } from '../store';
import { updateSettings } from './settingsStore';
import { getPins } from './pins';
import { getOrder } from './rowOrder';
import { arranged, groupSessions, type SessionGroup } from './sessionOrder';
import { inner, type TabsState } from './tabs';

export { groupSessions, ranked, type SessionGroup } from './sessionOrder';

export type { Organize };

export const useOrganize = (): Organize => useStore((s) => s.me?.settings.organize ?? 'program');

/** Changes it for the account: a phone's list here, the iPhone's and the CLI's (the desktop sidebar isn't grouped). */
export const setOrganize = (organize: Organize) => void updateSettings({ organize });

/** The order the sidebar keeps: as the person arranged it (ui/rowOrder.ts: a row dragged to a new place), else the
 *  order they were started; a new one last. It holds still: status shows on a row's mark, never by moving it (the
 *  machine's rank, what needs you first, is for the CLI's list). */
export const inOrder = (sessions: Session[]) => arranged(sessions, getOrder());

/** Groups (or the one list, not grouped) with the pinned sessions first in each, in the order kept; a pin moves its row and nothing else. */
export function pinnedFirst(groups: SessionGroup[]): SessionGroup[] {
  const pins = getPins();
  if (!pins.length) return groups;
  return groups.map((g) => ({ ...g, sessions: [...g.sessions.filter((s) => pins.includes(s.id)), ...g.sessions.filter((s) => !pins.includes(s.id))] }));
}

/** The sessions in the sidebar's order (⌘1–9, the palette, the empty pane): pinned first, then the order kept. With
 *  the workspace's groups, only the rows: a session another row holds (a tab added there, a pane split off it) has no
 *  row of its own. It holds still whatever is open. */
export function orderedSessions(sessions: Session[], tabs?: TabsState): Session[] {
  const list = pinnedFirst(groupSessions(inOrder(sessions), 'none'))[0]?.sessions ?? [];
  if (!tabs) return list;
  const held = inner(tabs);
  return list.filter((s) => !held.has(s.id));
}

/** The sessions ⌘1–9 count: the desktop sidebar's order. */
export const numberedSessions = orderedSessions;

/**
 * The sessions in the sidebar's order, held while `hold` (the pointer or a finger is on the
 * list, so nothing moves under it): the rows keep their places and their groups, a new one goes last, a gone
 * one leaves. Letting go moves them where they belong (the list animates it). A row the person just moved goes
 * there at once: that isn't the list moving under the pointer.
 */
export function useListOrder(sessions: Session[], hold: boolean): Session[] {
  const last = useRef<{ ids: number[]; groups: Map<number, NonNullable<Session['row']>['groups']>; order: readonly number[] }>({ ids: [], groups: new Map(), order: [] });
  const order = getOrder();
  if (!hold || last.current.order !== order) {
    const out = inOrder(sessions);
    last.current = { ids: out.map((s) => s.id), groups: new Map(out.flatMap((s) => (s.row ? [[s.id, s.row.groups] as const] : []))), order };
    return out;
  }
  const at = new Map(last.current.ids.map((id, i) => [id, i]));
  // a held row keeps its groups too, so it doesn't change place under the pointer
  const held = (s: Session): Session => {
    const groups = last.current.groups.get(s.id);
    return groups && s.row && s.row.groups !== groups ? { ...s, row: { ...s.row, groups } } : s;
  };
  return [...sessions].sort((a, b) => (at.get(a.id) ?? 1e9 + a.id) - (at.get(b.id) ?? 1e9 + b.id)).map(held);
}
